import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import {
  NotificationsActive,
  PhoneAndroid,
  CloudSync,
  CheckCircle,
  Schedule,
  Warning,
  Shield,
  Speed,
  Accessibility,
  TrendingUp,
  ArrowForward,
  Star,
  LocationOn,
  WhatsApp,
  Description,
  Update,
} from '@mui/icons-material';
import PWAInstallButton from '../components/PWAInstallButton';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Description sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Suivi des dossiers ANEF',
      description: 'Suivez en temps réel l\'état de vos dossiers ANEF (titres de séjour, naturalisation, etc.)',
    },
    {
      icon: <Update sx={{ fontSize: 40, color: '#2e7d32' }} />,
      title: 'Synchronisation ANEF automatique',
      description: 'Connexion directe avec votre compte ANEF pour récupérer automatiquement vos dossiers',
    },
    {
      icon: <NotificationsActive sx={{ fontSize: 40, color: '#ed6c02' }} />,
      title: 'Alertes compléments de dossier',
      description: 'Recevez une notification immédiate quand l\'ANEF demande un complément',
    },
    {
      icon: <PhoneAndroid sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Application Progressive (PWA)',
      description: 'Installez l\'application sur votre téléphone et accédez-y même hors ligne',
    },
    {
      icon: <Schedule sx={{ fontSize: 40, color: '#9c27b0' }} />,
      title: 'Historique complet ANEF',
      description: 'Consultez l\'historique de tous les événements de vos dossiers ANEF',
    },
    {
      icon: <Accessibility sx={{ fontSize: 40, color: '#2e7d32' }} />,
      title: 'Interface simplifiée',
      description: 'Plus besoin de vous connecter à l\'ANEF constamment, tout est centralisé',
    },
  ];

  const problems = [
    {
      icon: <Warning />,
      problem: 'Dossiers ANEF clôturés pour non-réponse aux compléments',
      solution: 'Recevez une notification immédiate dès que l\'ANEF demande un complément sur votre dossier',
    },
    {
      icon: <Warning />,
      problem: 'Connexion fréquente à l\'ANEF pour vérifier l\'état',
      solution: 'Vos dossiers ANEF sont synchronisés automatiquement, plus besoin de vous connecter constamment',
    },
    {
      icon: <Warning />,
      problem: 'Oubli des demandes de pièces complémentaires',
      solution: 'Notifications push sur votre téléphone pour chaque nouveau complément ANEF demandé',
    },
    {
      icon: <Warning />,
      problem: 'Difficulté à suivre plusieurs dossiers ANEF',
      solution: 'Tableau de bord centralisé avec tous vos dossiers ANEF (famille, titre de séjour, etc.)',
    },
  ];

  const pwaAdvantages = [
    {
      icon: <Speed />,
      title: 'Ultra-rapide',
      description: 'Chargement instantané grâce au cache intelligent',
    },
    {
      icon: <PhoneAndroid />,
      title: 'Mode hors ligne',
      description: 'Consultez vos données même sans connexion internet',
    },
    {
      icon: <Shield />,
      title: 'Sécurisé',
      description: 'Connexion chiffrée et données protégées',
    },
    {
      icon: <TrendingUp />,
      title: 'Léger',
      description: 'Aucun téléchargement lourd, installation en un clic',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="100% GRATUIT"
                color="success"
                icon={<Star />}
                sx={{ mb: 2, fontWeight: 'bold', fontSize: '0.9rem' }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                Ne ratez plus jamais un complément ANEF
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Système intelligent de suivi de vos dossiers ANEF avec notifications en temps réel.
                Soyez alerté instantanément de chaque demande de complément pour ne plus perdre vos dossiers.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/client/register')}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'grey.100',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  Commencer gratuitement
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/client/login')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      borderWidth: 2,
                    },
                  }}
                >
                  Se connecter
                </Button>
              </Stack>

              {/* Bouton d'installation PWA */}
              <Box sx={{ mt: 3 }}>
                <PWAInstallButton
                  variant="outlined"
                  size="large"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <PhoneAndroid sx={{ fontSize: { xs: 150, md: 250 }, opacity: 0.9 }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Problem & Solution Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{ fontWeight: 700, mb: 2 }}
        >
          Le problème ANEF que nous résolvons
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: '800px', mx: 'auto' }}
        >
          Chaque année, des milliers de dossiers ANEF sont clôturés pour non-réponse aux compléments
          simplement parce que les personnes ne savaient pas qu'un complément était demandé sur leur compte ANEF
        </Typography>

        <Grid container spacing={3}>
          {problems.map((item, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      bgcolor: 'error.light',
                      color: 'white',
                      p: 1.5,
                      borderRadius: 2,
                      display: 'flex',
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600, color: 'error.main' }}
                    >
                      {item.problem}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="body1" color="success.main" fontWeight={500}>
                      ✓ {item.solution}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 2 }}
          >
            Fonctionnalités principales
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Tout ce dont vous avez besoin pour suivre vos dossiers efficacement
          </Typography>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 1.5 }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ANEF Explanation Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
                Qu'est-ce que l'ANEF ?
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem', lineHeight: 1.8 }}>
                L'<strong>ANEF (Administration Numérique pour les Étrangers en France)</strong> est
                la plateforme officielle de l'État français pour gérer vos démarches administratives
                liées aux titres de séjour.
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem', lineHeight: 1.8 }}>
                Sur l'ANEF, vous pouvez suivre vos dossiers de :
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'white' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Renouvellement de titre de séjour"
                    primaryTypographyProps={{ fontSize: '1.05rem' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'white' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Première demande de titre"
                    primaryTypographyProps={{ fontSize: '1.05rem' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'white' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Changement de statut"
                    primaryTypographyProps={{ fontSize: '1.05rem' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'white' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Demandes de naturalisation"
                    primaryTypographyProps={{ fontSize: '1.05rem' }}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={6}
                sx={{
                  p: 4,
                  bgcolor: 'white',
                  borderRadius: 3,
                }}
              >
                <Typography variant="h5" color="error" sx={{ fontWeight: 700, mb: 2 }}>
                  ⚠️ Le problème majeur
                </Typography>
                <Typography variant="body1" color="text.primary" sx={{ mb: 2, lineHeight: 1.7 }}>
                  L'ANEF peut vous demander des <strong>compléments de dossier</strong> à tout moment.
                  Si vous ne répondez pas dans les délais, votre dossier sera automatiquement clôturé.
                </Typography>
                <Typography variant="body1" color="text.primary" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Le problème ? <strong>Aucune notification par SMS ou email !</strong> Vous devez
                  vous connecter régulièrement à l'ANEF pour vérifier.
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'success.light',
                    color: 'white',
                    p: 3,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    ✓ Notre solution
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    Nous nous connectons automatiquement à votre compte ANEF et vous alertons
                    instantanément par notification push dès qu'un complément est demandé.
                    Plus de stress, plus de dossiers perdus !
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* PWA Advantages Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{ fontWeight: 700, mb: 2 }}
        >
          Pourquoi une PWA?
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: '800px', mx: 'auto' }}
        >
          Les Progressive Web Apps combinent le meilleur des applications mobiles et du web
        </Typography>

        <Grid container spacing={4}>
          {pwaAdvantages.map((advantage, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 3,
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.light',
                    color: 'white',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {advantage.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {advantage.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {advantage.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={3}
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'info.light',
            color: 'white',
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Prêt à installer l'application?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.95 }}>
            Cliquez sur le bouton ci-dessous pour installer l'application sur votre téléphone
            ou voir les instructions détaillées selon votre navigateur.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <PWAInstallButton
              variant="contained"
              size="large"
            />
          </Box>

          <Typography variant="body2" sx={{ mt: 3, opacity: 0.8 }}>
            Compatible avec Chrome (Android) et Safari (iPhone)
          </Typography>
        </Paper>
      </Container>

      {/* Pricing Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 2 }}
          >
            Tarification simple et transparente
          </Typography>

          <Card
            sx={{
              mt: 4,
              p: 5,
              textAlign: 'center',
              border: '3px solid',
              borderColor: 'success.main',
            }}
          >
            <Chip
              label="OFFRE DE LANCEMENT"
              color="success"
              sx={{ mb: 2, fontWeight: 'bold' }}
            />
            <Typography
              variant="h2"
              sx={{ fontWeight: 800, color: 'success.main', mb: 2 }}
            >
              100% GRATUIT
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
              Pour une durée limitée
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Nous sommes actuellement en phase de lancement. Profitez de toutes les
              fonctionnalités gratuitement!
            </Typography>

            <List sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Notifications illimitées" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Suivi de tous vos dossiers" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Accès depuis tous vos appareils" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Support client prioritaire" />
              </ListItem>
            </List>

            <Box sx={{ mt: 4, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                L'application évoluera dans le temps avec de nouvelles fonctionnalités.
                Des options payantes pourraient être ajoutées à l'avenir, mais le service
                de base restera accessible à tous.
              </Typography>
            </Box>
          </Card>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper
          elevation={4}
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: 'white',
            borderRadius: 4,
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            Prêt à commencer?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
            Rejoignez-nous dès aujourd'hui et ne manquez plus jamais une mise à jour
            importante sur vos dossiers
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/client/register')}
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              px: 5,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'grey.100',
                transform: 'scale(1.05)',
              },
            }}
          >
            Créer mon compte gratuitement
          </Button>
        </Paper>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: 'grey.900',
          color: 'white',
          py: 6,
          mt: 4,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* À propos */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                À propos
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                Système de suivi ANEF développé pour vous aider à ne plus jamais rater
                une demande de complément sur vos dossiers administratifs.
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Service 100% gratuit en phase de lancement.
              </Typography>
            </Grid>

            {/* Contact */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Contact
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ fontSize: 20, color: 'primary.light' }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Combani, en face de la pharmacie
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WhatsApp sx={{ fontSize: 20, color: '#25D366' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.8,
                      cursor: 'pointer',
                      '&:hover': { color: '#25D366' },
                    }}
                    component="a"
                    href="https://wa.me/262639778600"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    +262 639 77 86 00
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                startIcon={<WhatsApp />}
                href="https://wa.me/262639778600"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  mt: 2,
                  bgcolor: '#25D366',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#20BA5A',
                  },
                }}
              >
                Contactez-nous sur WhatsApp
              </Button>
            </Grid>

            {/* Créé par */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Créé par
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'rgba(255,255,255,0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.light', mb: 1 }}>
                  Cyber Mangaza
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                  Votre partenaire en services numériques
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  Solutions innovantes pour simplifier vos démarches administratives
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="text"
                onClick={() => navigate('/cgu')}
                sx={{
                  color: 'grey.400',
                  '&:hover': {
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                Conditions Générales d'Utilisation
              </Button>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              © 2025 Cyber Mangaza - Système de Suivi ANEF - Tous droits réservés
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
              Service gratuit - En phase de lancement
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
